import { PostgrestError } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { DbTask, TaskInsert, TaskUpdate } from './types';

/**
 * Fetches tasks for the current user with optional date range filtering
 * Now includes assignment data with full profile information using foreign key constraint
 * NOTE: This function relies on RLS policies for access control - not ideal for scalability
 * RECOMMENDED: Use fetchAssignedTasks() for TaskManager page (database-level filtering)
 */
export const fetchTasks = async (userId: string, options?: {
  startDate?: string;
  endDate?: string;
  strictDateRange?: boolean;
  noDateFilter?: boolean;
}): Promise<{ data: DbTask[] | null; error: PostgrestError | null }> => {
  // Enhanced query with full assignee profile data using foreign key constraint
  let query = supabase
    .from('planner_tasks')
    .select(`
      *,
      planner_tasks_assignees!left(
        assignee_id,
        assigned_at,
        assigned_by,
        public_profile!fk_planner_tasks_assignees_assignee_profile(
          id,
          first_name,
          username,
          profile_picture_url
        )
      )
    `);
    
  // Apply date range filter if provided AND not bypassed
  if (options?.startDate && options?.endDate && !options?.noDateFilter) {
    if (options.strictDateRange) {
      // "Now" mode: Show ONLY tasks due today (override date range to today)
      const today = new Date();
      const todayStart = today.toISOString().split('T')[0]; // YYYY-MM-DD format
      const todayEnd = todayStart; // Same day for start and end
      
      query = query.or(
        `and(due_date.gte.${todayStart},due_date.lte.${todayEnd}),due_date.is.null`
      );
    } else {
      // "All" mode: Show all tasks regardless of completion status or due date
      // No date filtering - return everything for the user
    }
  }
  
  return await query
    .order('card_order', { ascending: false })
    .order('created_at', { ascending: false });
};

/**
 * Fetches a single task by ID
 */
export const fetchTaskById = async (taskId: string): Promise<{ data: DbTask | null; error: PostgrestError | null }> => {
  const { data, error } = await supabase
    .from('planner_tasks')
    .select('*')
    .eq('id', taskId)
    .single();

  return { data: data as DbTask, error };
};

/**
 * Fetches only tasks assigned to the current user (for TaskManager page)
 * Uses INNER JOIN for database-level filtering - only shows tasks where user is assignee
 * Now includes ALL assignee profile data using the foreign key constraint
 * Does NOT show tasks owned by user unless they are also assigned
 * SCALABLE: Perfect example of frontend filtering approach - no RLS dependency
 */
export const fetchAssignedTasks = async (userId: string): Promise<{ data: DbTask[] | null; error: PostgrestError | null }> => {
  console.log('[fetchAssignedTasks] Fetching tasks assigned to user:', userId);
  
  // Query for tasks where user is assigned with full assignee profile data
  // Uses the new foreign key constraint for efficient profile data fetching
  const result = await supabase
    .from('planner_tasks')
    .select(`
      *,
      planner_tasks_assignees!inner(
        assignee_id,
        assigned_at,
        assigned_by,
        public_profile!fk_planner_tasks_assignees_assignee_profile(
          id,
          first_name,
          username,
          profile_picture_url
        )
      )
    `)
    .eq('planner_tasks_assignees.assignee_id', userId)
    .order('card_order', { ascending: false })
    .order('created_at', { ascending: false });
    
  console.log('[fetchAssignedTasks] Database returned:', { 
    taskCount: result.data?.length || 0, 
    error: !!result.error,
    firstTaskSample: result.data?.[0] ? {
      id: result.data[0].id,
      title: result.data[0].title,
      user_id: result.data[0].user_id,
      assignees: result.data[0].planner_tasks_assignees
    } : null
  });
  
  return result;
};

/**
 * Gets the minimum card order value for creating new tasks
 * Using negative values allows us to add tasks to bottom consistently
 */
export const getMinCardOrder = async (userId: string): Promise<number> => {
  // Note: RLS policies handle access control (owned + collaborative access)
  const { data, error } = await supabase
    .from('planner_tasks')
    .select('card_order')
    .order('card_order', { ascending: true })
    .limit(1);
    
  if (error || !data || data.length === 0) {
    return 0; // Default if no tasks exist
  }
  
  return data[0].card_order - 100000; // Place 100000 below the lowest
};

/**
 * Creates a new task
 */
export const createTask = async (task: TaskInsert): Promise<{ data: DbTask | null; error: PostgrestError | null }> => {
  // If card_order isn't provided, get a minimum order to place at bottom
  if (task.card_order === undefined) {
    task.card_order = await getMinCardOrder(task.user_id);
  }
  
  return await supabase
    .from('planner_tasks')
    .insert(task)
    .select()
    .single();
};

/**
 * Updates a task
 */
export const updateTask = async (taskId: string, updates: TaskUpdate): Promise<{ data: DbTask | null; error: PostgrestError | null }> => {
  return await supabase
    .from('planner_tasks')
    .update(updates)
    .eq('id', taskId)
    .select()
    .single();
};

/**
 * Updates a task's card order
 */
export const updateTaskOrder = async (taskId: string, newOrder: number): Promise<{ data: DbTask | null; error: PostgrestError | null }> => {
  console.log(`Updating task ${taskId} order to ${newOrder}`);
  return await updateTask(taskId, { card_order: newOrder });
};

/**
 * Completes a task by setting is_completed to true
 */
export const completeTask = async (taskId: string): Promise<{ data: DbTask | null; error: PostgrestError | null }> => {
  return await updateTask(taskId, { is_completed: true });
};

/**
 * Uncompletes a task by setting is_completed to false
 */
export const uncompleteTask = async (taskId: string): Promise<{ data: DbTask | null; error: PostgrestError | null }> => {
  return await updateTask(taskId, { is_completed: false });
};

/**
 * Deletes a task
 */
export const deleteTask = async (taskId: string): Promise<{ error: PostgrestError | null }> => {
  return await supabase
    .from('planner_tasks')
    .delete()
    .eq('id', taskId);
};

/**
 * Calculate a new order value between two tasks
 */
export const calculateOrderBetween = (beforeOrder: number | undefined, afterOrder: number | undefined): number => {
  // Default values if we don't have before/after tasks
  const before = beforeOrder !== undefined ? beforeOrder : 0;
  const after = afterOrder !== undefined ? afterOrder : -1000000;
  
  // Calculate middle point
  return Math.floor((before + after) / 2);
};
